import UserState from "@/stores/UserState";
import type {
  Diagnostic,
  Report,
  UpdateReportPayload,
  DRBranchWithContributors,
  DrawingImage,
  DRDrawing,
  GetDrawingsFiltersParams,
  MetadataDetector,
  MetadataDetectorPayload,
  OdaBinaryFormat,
  PaginationParams,
  UpdateDiagnosticPayload,
  UpdateDrawingPayload,
} from "@/types";
import ky, { type Options } from "ky";
import type { ApiDrawingDto, ApiHealth } from "@/repositories/api.types";
import type { ApiActivityEventDto } from "@/types/activity-event.types";
import type { ObservationPlanContextMap } from "@/composable/useObservationPlanLinks";
import type {
  PlanAnalysisAiRequest,
  PlanAnalysisAiResult,
  PlanGroupingRequest,
  PlanGroupingResponse
} from "@/types/plan-analysis.types";
import { DrawingDecoder } from "@/utils/DrawingDecoder";
import { handleUnauthorized, resetSessionGuard } from "@/utils/session-guard";
import type { Legend } from "./types/legend.types";
import type {
  Organization,
  UpdateMyOrganizationPayload,
} from "./types/organizations.types";
import $ls, { LocalStorageKey } from "./utils/local-storage";
import type {
  ProjectPayload,
  Project,
  GetProjectsFilters,
} from "./types/projects.types";
import type { LicenseSummary } from "./types/licenses.types";
import type {
  ReportTemplate,
  UpdateReportTemplatePayload,
} from "./types/report-templates.types";
import type {
  FileEntity,
  FileParams,
  FileType,
  FinalizeUploadResponse,
} from "./types/file.types";
import type {
  CreateSectionPayload,
  Section,
  UpdateSectionPayload,
} from "./types/sections.types";
import type {
  CreateFindingPayload,
  Finding,
  UpdateFindingPayload,
} from "./types/findings.types";
import type {
  Catalogue,
  CataloguePart,
  DisorderTemplate,
  FetchDisorderTemplatesFilters,
} from "./types/catalogues.types";
import type {
  DiagnosticImage,
  FetchDiagnosticImagesParams,
} from "./types/diagnostic-images.types";
import type {
  ReportImage,
  FetchReportImagesParams,
  UpdateReportImagePayload,
} from "./types/report-images.types";
import type {
  AudioRecording,
  CreateAudioRecordingPayload,
  CreateFieldObservationPayload,
  CreateFieldPartPayload,
  CreateFieldPartTemplatePayload,
  FieldObservation,
  FieldPart,
  FieldPartTemplate,
  SyncFieldObservationPayload,
  UpdateAudioRecordingPayload,
  UpdateFieldObservationPayload,
  UpdateFieldPartPayload,
  UpdateFieldPartTemplatePayload,
} from "./types/field-observations.types";

export type UserProperties = {
  id: number;
  email: string;
  full_name: string;
  joined_at: string;
  role: UserRole;
  enabled: boolean;
  legends: Legend[];
  drawingsCount?: number;
  organization?: Organization;
};

export type UserRole = "USER" | "ADMIN" | "SUPERADMIN" | "MANAGER";
export const userRolesList: UserRole[] = [
  "SUPERADMIN",
  "ADMIN",
  "MANAGER",
  "USER",
];

type UserAuthReturnDto = {
  user: UserProperties;
  jwt: string;
};

type Primitive = string | number | boolean | null | undefined;

type SearchParamsObject = Record<string, Primitive | Primitive[]>;

class APIClient {
  apiURL = import.meta.env.DR_API_URL;

  client = ky.extend({
    prefixUrl: this.apiURL,
    hooks: {
      beforeError: [
        async (error) => {
          // @ts-ignore
          let bodyAsJson: object = error.response.body;
          if (error.response.body instanceof ReadableStream) {
            try {
              bodyAsJson = await error.response.json();
            } catch {
              bodyAsJson = {};
            }
          }
          if (error.response.status === 401) {
            // The session is only destroyed when the API says the credentials
            // are at fault. See session-guard.
            await handleUnauthorized(bodyAsJson);
          }
          throw new APIError(error.response.status, bodyAsJson);
        },
      ],
      beforeRequest: [
        (request) => {
          if (!request.url.includes("health")) {
            const jwt = $ls.getItem(LocalStorageKey.JWT);
            if (jwt) {
              request.headers.set("Authorization", `Bearer ${jwt}`);
            }
          }
          // Bypass tunnel protection pages (ngrok/localtunnel)
          request.headers.set("Bypass-Tunnel-Reminder", "true");
        },
      ],
    },
  });

  async get<R>(uri: string, options?: Options) {
    return this.client.get(uri, options).json<R>();
  }

  async put<R>(uri: string, body?: object, params?: Options) {
    return this.client.put(uri, { json: body, ...params }).json<R>();
  }

  async delete<R>(uri: string, body?: object, params?: Options) {
    return this.client.delete(uri, { json: body, ...params }).json<R>();
  }

  async post<R>(uri: string, body?: object, params?: Options) {
    return this.client.post(uri, { json: body, ...params }).json<R>();
  }

  private normalizeSearchParams(
    sp?: string | URLSearchParams | SearchParamsObject
  ): string | URLSearchParams | undefined {
    if (!sp) return undefined;

    if (typeof sp === "string" || sp instanceof URLSearchParams) {
      return sp;
    }

    const searchParams = new URLSearchParams();

    Object.entries(sp).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      if (Array.isArray(value)) {
        for (const v of value) {
          if (v === undefined || v === null) continue;
          searchParams.append(`${key}[]`, String(v));
        }
      } else {
        searchParams.set(key, String(value));
      }
    });

    return searchParams;
  }

  // AUTH

  static saveJwt(jwt: string) {
    $ls.setItem(LocalStorageKey.JWT, jwt);
  }

  static updateLocalUser(user: UserProperties, jwt: string) {
    APIClient.saveJwt(jwt);
    UserState.updateAndPersist(user);
    resetSessionGuard();
  }

  static parseAuthPayload(
    user: UserProperties | UserAuthReturnDto
  ): UserAuthReturnDto {
    function isAuthPayload(
      payload: UserAuthReturnDto | unknown
    ): payload is UserAuthReturnDto {
      // @ts-ignore
      return Boolean(payload.user);
    }

    if (isAuthPayload(user)) return user;
    return { user, jwt: "" };
  }

  async forgot(email: string) {
    await this.post("auth/forgot", {
      email,
    });
  }

  async reset(token: string, password: string) {
    await this.post("auth/reset", {
      password,
      token,
    });
  }

  async addMember(
    full_name: string,
    role: string,
    email: string,
    password: string
  ): Promise<UserProperties> {
    return await this.post<UserProperties>("admin/users/add-member", {
      full_name,
      role,
      email,
      password,
    });
  }

  async login(email: string, password: string): Promise<void> {
    const payload = await this.post<UserAuthReturnDto | UserProperties>(
      "auth/login",
      {
        email,
        password,
      }
    );
    const { user, jwt } = APIClient.parseAuthPayload(payload);
    APIClient.updateLocalUser(user, jwt);
  }

  async getMe(): Promise<UserProperties> {
    const payload = await this.get<UserProperties>("auth/me");
    const { user } = APIClient.parseAuthPayload(payload);
    return user;
  }

  // DRAWINGS

  async fetchDrawings(
    bodyParams: PaginationParams & GetDrawingsFiltersParams
  ): Promise<DRDrawing[]> {
    const res = await this.client
      .post("api/drawings/search", { json: bodyParams })
      .json<ApiDrawingDto[]>();

    // @todo do proper validation with a typescript lib (requires strict: true)
    return res.map((d) => DrawingDecoder.fromApiDtoToDomain(d));
  }

  async createDrawing(
    fileName: string,
    projectId: number,
    options: {
      firstRevisionOpenCloudFileId?: string;
      firstRevisionFileId?: number;
      rasterPaths?: string[];
    }
  ): Promise<DRDrawing> {
    const body: Record<string, unknown> = {
      fileName,
      projectId,
      rasterPaths: options.rasterPaths,
    };

    // For DWG format, use fileId (S3 storage)
    // For VSFX format, use openCloudFileId (OCS storage)
    if (options.firstRevisionFileId) {
      body.firstRevisionFileId = options.firstRevisionFileId;
    } else if (options.firstRevisionOpenCloudFileId) {
      body.firstRevisionOpenCloudFileId = options.firstRevisionOpenCloudFileId;
    }

    const drawing = await this.post<ApiDrawingDto>("api/drawings", body);
    return DrawingDecoder.fromApiDtoToDomain(drawing);
  }

  async createRevision(
    drawingId: number,
    revisionOdaFileId: string | null,
    format: OdaBinaryFormat,
    currentRevisionId: number,
    rasterPaths?: string[],
    fromDevice?: string,
    fileId?: number
  ) {
    const body: Record<string, unknown> = {
      format,
      ancestorRevisionId: currentRevisionId,
      rasterPaths,
      fromDevice,
    };

    // For DWG format, use fileId (S3 storage)
    // For VSFX/VSF format, use openCloudFileId (OCS storage)
    if (format === "DWG" && fileId) {
      body.fileId = fileId;
    } else if (revisionOdaFileId) {
      body.openCloudFileId = revisionOdaFileId;
    }

    const { updated } = await this.post<{
      updated: ApiDrawingDto;
    }>(`api/drawings/${drawingId}/revisions`, body);

    return {
      drawing: DrawingDecoder.fromApiDtoToDomain(updated),
    };
  }

  /**
   * Get presigned download URL for a DWG revision stored on S3.
   */
  async getRevisionDownloadUrl(
    drawingId: number,
    revisionId: number
  ): Promise<{ url: string; fileName: string }> {
    return await this.get<{ url: string; fileName: string }>(
      `api/drawings/${drawingId}/revisions/${revisionId}/download`
    );
  }

  /** Télécharge le DWG d'une révision (URL présignée S3). */
  async downloadRevisionDwg(
    drawingId: number,
    revisionId: number
  ): Promise<ArrayBuffer> {
    const { url } = await this.getRevisionDownloadUrl(drawingId, revisionId);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Téléchargement de la révision ${revisionId} : ${response.status} ${response.statusText}`
      );
    }
    return response.arrayBuffer();
  }

  /** Oplog : events de la lignée d'une révision head (DAG résolu côté serveur). */
  async getActivityEventsByLineage(
    headRevisionId: number
  ): Promise<ApiActivityEventDto[]> {
    return await this.get<ApiActivityEventDto[]>(
      `api/activity-events/lineage/${headRevisionId}`
    );
  }

  /** Oplog : pousse les events non synchronisés (idempotent par eventId). */
  async syncActivityEvents(
    events: Array<{
      eventId: string;
      revisionId: number;
      entityUid: string;
      type: string;
      clientTs: string;
      snapshot?: unknown;
    }>
  ): Promise<{ synced: number }> {
    return await this.post<{ synced: number }>(`api/activity-events/sync`, {
      events,
    });
  }

  //!deprecated
  async forkDrawing(
    drawingId: number,
    revisionOdaFileId: string,
    rasterPaths?: string[]
  ): Promise<DRDrawing> {
    const drawing = await this.post<ApiDrawingDto>(
      `api/drawings/${drawingId}/fork`,
      {
        openCloudFileId: revisionOdaFileId,
        rasterPaths,
      }
    );
    return DrawingDecoder.fromApiDtoToDomain(drawing);
  }

  async updateDrawing(
    drawingId: number,
    payload: UpdateDrawingPayload
  ): Promise<ApiDrawingDto> {
    return await this.put<ApiDrawingDto>(`api/drawings/${drawingId}`, payload);
  }

  async archiveDrawing(drawingId: number): Promise<void> {
    await this.post(`api/drawings/${drawingId}/archive`);
    return;
  }

  async unArchiveDrawing(drawingId: number): Promise<void> {
    return await this.post(`api/drawings/${drawingId}/unarchive`);
  }

  async deleteDrawing(drawingId: number): Promise<void> {
    await this.post(`api/drawings/${drawingId}/delete`);
    return;
  }

  async fetchDrawingImages(payload: {
    rasterPaths?: string[];
    drawingId?: number;
  }): Promise<DrawingImage[]> {
    return await this.get<DrawingImage[]>(`api/drawings/images`, {
      searchParams: this.normalizeSearchParams(payload),
    });
  }

  async getDrawingImagesDownloadUrls(drawingId: number) {
    return await this.get<{ urls: string[] }>(
      `api/drawings/${drawingId}/images/download-urls`
    );
  }

  async fetchDrawingBranches(
    drawingId: number
  ): Promise<DRBranchWithContributors[]> {
    return await this.get<DRBranchWithContributors[]>(
      `api/drawings/${drawingId}/branches`
    );
  }

  /** Ancêtre commun de deux révisions (base de la fusion 3 voies). */
  async getMergeBase(
    drawingId: number,
    targetRevisionId: number,
    mergedRevisionId: number
  ): Promise<{ baseRevisionId: number }> {
    return await this.get<{ baseRevisionId: number }>(
      `api/drawings/${drawingId}/merge-base`,
      { searchParams: { targetRevisionId, mergedRevisionId } }
    );
  }

  /**
   * Révision de fusion : la branche de `mergedRevisionId` est absorbée par celle
   * de `targetRevisionId`. 409 (`code: "STALE"`) si une tête a bougé entre-temps.
   */
  async mergeRevisions(
    drawingId: number,
    body: {
      fileId: number;
      targetRevisionId: number;
      mergedRevisionId: number;
      fromDevice?: string;
      uidAliases?: { fromUid: string; toUid: string }[];
    }
  ): Promise<DRDrawing> {
    const { updated } = await this.post<{ updated: ApiDrawingDto }>(
      `api/drawings/${drawingId}/merge`,
      body
    );
    return DrawingDecoder.fromApiDtoToDomain(updated);
  }

  async switchDrawingBranch(
    drawingId: number,
    branchId: number
  ): Promise<void> {
    await this.post(`api/drawings/${drawingId}/switch-branch`, { branchId });
  }

  async renameBranch(
    drawingId: number,
    branchId: number,
    name: string
  ): Promise<void> {
    await this.put(`api/drawings/${drawingId}/branches/${branchId}`, { name });
  }

  // USER
  async toggleUser(userId: number, organizationId?: number): Promise<void> {
    await this.put(`admin/users/${userId}/toggle`, {
      organizationId,
    });
  }

  async deleteUser(userId: number, organizationId?: number): Promise<void> {
    await this.delete(`admin/users/${userId}`, {
      organizationId,
    });
  }

  async fetchAllUsers(): Promise<UserProperties[]> {
    return await this.get("admin/users");
  }

  // ORGANIZATIONS
  async getMyLicenseSummary(): Promise<LicenseSummary> {
    return await this.get(`api/organizations/mine/license-summary`);
  }

  async updateMyOrganization(
    payload: UpdateMyOrganizationPayload
  ): Promise<Organization> {
    return await this.put(`api/organizations/mine`, payload);
  }

  // LEGENDS
  async fetchAllLegends(): Promise<Legend[]> {
    return await this.get(`api/legends/all`);
  }
  async fetchMyLegends(): Promise<Legend[]> {
    return await this.get(`api/legends`);
  }

  async fetchLegend(id: number): Promise<Legend> {
    return await this.get(`api/legends/${id}`);
  }

  async updateLegend(id: number, payload: Partial<Legend>): Promise<Legend> {
    return await this.put(`api/legends/${id}`, payload);
  }

  async updateLegendUsers(id: number, users: number[]): Promise<Legend> {
    return await this.put(`api/legends/${id}/users`, { users });
  }

  async createLegend(legend: Omit<Legend, "id">): Promise<Legend> {
    return await this.post(`api/legends`, legend);
  }

  async deleteLegend(id: number): Promise<void> {
    await this.delete(`api/legends/${id}`);
  }

  async duplicateLegend(id: number): Promise<Legend> {
    return await this.post(`api/legends/${id}/duplicate`);
  }

  async linkLegendDwg(
    legendId: number,
    fileId: number
  ): Promise<{ dwg_file_info: Legend["dwg_file_info"] }> {
    return await this.post(`api/legends/${legendId}/dwg`, { fileId });
  }

  async unlinkLegendDwg(legendId: number): Promise<void> {
    await this.delete(`api/legends/${legendId}/dwg`);
  }

  // METADATA DETECTORS
  async fetchAllMetadataDetectors(params: {
    isDefault?: boolean;
  }): Promise<MetadataDetector[]> {
    return await this.get(`api/metadata/`, { searchParams: params });
  }

  async updateMetadataDetector(
    id: number,
    payload: Partial<MetadataDetectorPayload>
  ): Promise<MetadataDetector> {
    return await this.put(`api/metadata/${id}`, payload);
  }

  async createMetadataDetector(
    MetadataDetector: MetadataDetectorPayload
  ): Promise<MetadataDetector> {
    return await this.post(`api/metadata`, MetadataDetector);
  }

  async deleteMetadataDetector(id: number): Promise<void> {
    await this.delete(`api/metadata/${id}`);
  }

  // FINDINGS

  async createFinding(payload: CreateFindingPayload): Promise<Finding> {
    return await this.post(`api/findings`, payload);
  }

  async updateFinding(
    id: number,
    payload: UpdateFindingPayload
  ): Promise<Finding> {
    return await this.put(`api/findings/${id}`, payload);
  }

  async moveFinding(id: number, sectionId: number | null): Promise<Finding> {
    return await this.put(`api/findings/${id}/move`, { sectionId });
  }

  async deleteFinding(id: number): Promise<any> {
    await this.delete(`api/findings/${id}`);
  }

  async fetchUnsortedFindings(reportId: number): Promise<Finding[]> {
    return await this.get(`api/findings/unsorted/${reportId}`);
  }

  // FIELD OBSERVATIONS

  async getFieldObservations(
    diagnosticId: number
  ): Promise<FieldObservation[]> {
    return await this.get(`api/field-observations/diagnostic/${diagnosticId}`);
  }

  async getFieldObservation(id: number): Promise<FieldObservation> {
    return await this.get(`api/field-observations/${id}`);
  }

  async createFieldObservation(
    payload: CreateFieldObservationPayload
  ): Promise<FieldObservation> {
    return await this.post(`api/field-observations`, payload);
  }

  async updateFieldObservation(
    id: number,
    payload: UpdateFieldObservationPayload
  ): Promise<FieldObservation> {
    return await this.put(`api/field-observations/${id}`, payload);
  }

  async deleteFieldObservation(id: number): Promise<void> {
    await this.delete(`api/field-observations/${id}`);
  }

  async syncFieldObservations(
    observations: SyncFieldObservationPayload[]
  ): Promise<FieldObservation[]> {
    return await this.post(`api/field-observations/sync`, { observations });
  }

  // AUDIO RECORDINGS

  async getAudioRecordings(observationId: number): Promise<AudioRecording[]> {
    return await this.get(
      `api/field-observations/${observationId}/audio-recordings`
    );
  }

  async createAudioRecording(
    payload: CreateAudioRecordingPayload
  ): Promise<AudioRecording> {
    return await this.post(`api/field-observations/audio-recordings`, payload);
  }

  async updateAudioRecording(
    id: number,
    payload: UpdateAudioRecordingPayload
  ): Promise<AudioRecording> {
    return await this.put(
      `api/field-observations/audio-recordings/${id}`,
      payload
    );
  }

  async deleteAudioRecording(id: number): Promise<void> {
    await this.delete(`api/field-observations/audio-recordings/${id}`);
  }

  // FIELD PARTS

  async getFieldParts(diagnosticId: number): Promise<FieldPart[]> {
    return await this.get(
      `api/field-observations/parts/diagnostic/${diagnosticId}`
    );
  }

  async createFieldPart(payload: CreateFieldPartPayload): Promise<FieldPart> {
    return await this.post(`api/field-observations/parts`, payload);
  }

  async updateFieldPart(
    id: number,
    payload: UpdateFieldPartPayload
  ): Promise<FieldPart> {
    return await this.put(`api/field-observations/parts/${id}`, payload);
  }

  async deleteFieldPart(id: number): Promise<void> {
    await this.delete(`api/field-observations/parts/${id}`);
  }

  async duplicateFieldPart(id: number, names: string[]): Promise<FieldPart[]> {
    return await this.post(`api/field-observations/parts/${id}/duplicate`, {
      names,
    });
  }

  async applyFieldPartTemplate(
    diagnosticId: number,
    templateId: number,
    parentPartId?: number
  ): Promise<FieldPart[]> {
    return await this.post(
      `api/field-observations/parts/apply-template/${diagnosticId}`,
      { templateId, parentPartId }
    );
  }

  // FIELD PART TEMPLATES

  async fetchFieldPartTemplates(): Promise<FieldPartTemplate[]> {
    return await this.get(`api/field-part-templates`);
  }

  async fetchFieldPartTemplate(id: number): Promise<FieldPartTemplate> {
    return await this.get(`api/field-part-templates/${id}`);
  }

  async createFieldPartTemplate(
    payload: CreateFieldPartTemplatePayload
  ): Promise<FieldPartTemplate> {
    return await this.post(`api/field-part-templates`, payload);
  }

  async updateFieldPartTemplate(
    id: number,
    payload: UpdateFieldPartTemplatePayload
  ): Promise<FieldPartTemplate> {
    return await this.put(`api/field-part-templates/${id}`, payload);
  }

  async deleteFieldPartTemplate(id: number): Promise<void> {
    await this.delete(`api/field-part-templates/${id}`);
  }

  // DIAGNOSTIC IMAGES

  async getDiagnosticImages(
    searchParams: FetchDiagnosticImagesParams
  ): Promise<DiagnosticImage[]> {
    return await this.get(`api/diagnostic-images`, { searchParams });
  }

  async getDiagnosticImage(id: number): Promise<DiagnosticImage> {
    return await this.get(`api/diagnostic-images/${id}`);
  }

  async updateDiagnosticImage(
    id: number,
    payload: { photoNumber?: number | null }
  ): Promise<DiagnosticImage> {
    return await this.client
      .patch(`api/diagnostic-images/${id}`, { json: payload })
      .json<DiagnosticImage>();
  }

  async deleteDiagnosticImage(id: number): Promise<void> {
    return await this.delete(`api/diagnostic-images/${id}`);
  }

  async deleteManyDiagnosticImages(
    ids: number[]
  ): Promise<{ deleted: number[]; failed: number[] }> {
    return await this.delete(`api/diagnostic-images/batch`, { ids });
  }

  // REPORT IMAGES

  async getReportImages(
    searchParams: FetchReportImagesParams
  ): Promise<ReportImage[]> {
    return await this.get(`api/report-images`, { searchParams });
  }

  async getReportImage(id: number): Promise<ReportImage> {
    return await this.get(`api/report-images/${id}`);
  }

  async createReportImage(payload: {
    diagnosticImageId: number;
    reportId: number;
    numbering?: number;
    description?: string;
    location?: string;
  }): Promise<ReportImage> {
    return await this.post(`api/report-images`, payload);
  }

  async createReportImagesFromDiagnostic(
    reportId: number
  ): Promise<ReportImage[]> {
    return await this.post(`api/report-images/batch?reportId=${reportId}`);
  }

  async updateReportImage(
    id: number,
    payload: UpdateReportImagePayload
  ): Promise<ReportImage> {
    return await this.put(`api/report-images/${id}`, payload);
  }

  async deleteReportImage(id: number): Promise<void> {
    return await this.delete(`api/report-images/${id}`);
  }

  async sortReportImageToSection(
    imageId: number,
    sectionId: number | null
  ): Promise<ReportImage> {
    return await this.post(`api/report-images/${imageId}/sort`, {
      sectionId,
    });
  }

  async excludeReportImage(id: number): Promise<ReportImage> {
    return await this.post(`api/report-images/${id}/exclude`);
  }

  async restoreReportImage(id: number): Promise<ReportImage> {
    return await this.post(`api/report-images/${id}/restore`);
  }

  async addReportImageToFinding(
    imageId: number,
    findingId: number
  ): Promise<ReportImage> {
    return await this.post(
      `api/report-images/${imageId}/findings/${findingId}`
    );
  }

  async addManyReportImagesToFinding(
    findingId: number,
    imageIds: number[]
  ): Promise<ReportImage[]> {
    return await this.post(`api/report-images/batch/findings/${findingId}`, {
      imageIds,
    });
  }

  async removeReportImageFromFinding(
    imageId: number,
    findingId: number
  ): Promise<ReportImage> {
    return await this.delete(
      `api/report-images/${imageId}/findings/${findingId}`
    );
  }

  async setReportImageOverview(
    imageId: number,
    isOverview: boolean
  ): Promise<ReportImage> {
    return await this.post(`api/report-images/${imageId}/set-overview`, {
      isOverview,
    });
  }

  async setReportImageOverviewSection(
    imageId: number,
    sectionId: number | null
  ): Promise<ReportImage> {
    return await this.post(
      `api/report-images/${imageId}/set-overview-section`,
      {
        sectionId,
      }
    );
  }

  async smartRenumberReportImages(
    reportId: number,
    mode: "appearance" | "identifier" = "appearance"
  ): Promise<{ renumberedCount: number; unnumberedCount: number }> {
    return await this.post(
      `api/report-images/smart-renumber?reportId=${reportId}`,
      { mode }
    );
  }

  // EVENT LOGS

  async createEventLogs(
    payload: {
      type: number;
      date?: Date;
      context?: Record<string, string | number | boolean | null>;
    }[]
  ) {
    return await this.post(`api/events`, payload);
  }

  // PROJECTS

  async fetchProjects(
    searchParams: PaginationParams & GetProjectsFilters
  ): Promise<{
    projects: Project[];
    totalCount: number;
  }> {
    return await this.get(`api/projects`, {
      searchParams: this.normalizeSearchParams(searchParams),
    });
  }

  async fetchProjectById(id: number): Promise<Project> {
    return await this.get(`api/projects/${id}`);
  }

  async createProject(project: ProjectPayload): Promise<Project> {
    return await this.post(`api/projects`, project);
  }

  async updateProject(
    id: number,
    payload: Partial<ProjectPayload>
  ): Promise<Project> {
    return await this.put(`api/projects/${id}`, payload);
  }

  // DIAGNOSTICS
  async fetchAllDiagnostics(
    params: {
      projectId?: number;
      isExtractingData?: boolean;
    } & PaginationParams
  ): Promise<Diagnostic[]> {
    return await this.get(`api/diagnostics`, {
      searchParams: params,
    });
  }

  async fetchDiagnostic(id: number): Promise<Diagnostic> {
    return await this.get(`api/diagnostics/${id}`);
  }

  async deleteManyDiagnostics(ids: number[]): Promise<void> {
    await this.post(`api/diagnostics/delete-many`, { ids });
  }

  async startDiagnosticDataExtraction(
    id: number,
    reportTemplateId: number | undefined
  ): Promise<void> {
    return await this.post(`api/diagnostics/${id}/extract-data`, {
      reportTemplateId,
    });
  }
  async updateDiagnostic(
    id: number,
    payload: UpdateDiagnosticPayload
  ): Promise<Diagnostic> {
    return await this.put(`api/diagnostics/${id}`, payload);
  }

  async createDiagnostic(projectId: number, name?: string): Promise<Diagnostic> {
    return await this.post(`api/diagnostics`, {
      projectId,
      ...(name?.trim() ? { name: name.trim() } : {}),
    });
  }

  async applyPartsSuggestions(
    diagnosticId: number
  ): Promise<{ success: boolean }> {
    return await this.post(
      `api/diagnostics/${diagnosticId}/apply-parts-suggestions`
    );
  }

  async startFindingsGeneration(
    diagnosticId: number,
    planContext?: ObservationPlanContextMap
  ): Promise<{ isGenerating: boolean; reportId: number }> {
    return await this.post(
      `api/diagnostics/${diagnosticId}/generate-findings`,
      planContext ? { planContext } : {}
    );
  }

  /**
   * Passe IA n°1 de l'analyse du plan : l'IA tranche le découpage des
   * entités en observations (proposition locale + doutes en prior).
   */
  async analyzePlanGrouping(
    drawingId: number,
    payload: PlanGroupingRequest
  ): Promise<PlanGroupingResponse> {
    return await this.post(`api/drawings/${drawingId}/analyze/grouping`, payload, { timeout: 120_000 });
  }

  /**
   * Passe IA n°2 : rédige intitulé/désordre/partie/constat pour des groupes
   * donnés — ré-appelable après correction manuelle.
   */
  async describePlanObservations(
    drawingId: number,
    payload: PlanAnalysisAiRequest
  ): Promise<PlanAnalysisAiResult> {
    return await this.post(`api/drawings/${drawingId}/analyze/describe`, payload, { timeout: 120_000 });
  }

  async getFindingsGenerationStatus(diagnosticId: number): Promise<{
    isGenerating: boolean;
    step: string | null;
    error: string | null;
  }> {
    return await this.get(`api/diagnostics/${diagnosticId}/generation-status`);
  }

  // Modular generation
  async startModularGeneration(
    diagnosticId: number,
    reportId: number,
    modules: string[]
  ): Promise<{ isGenerating: boolean; modules: string[] }> {
    return await this.post(`api/diagnostics/${diagnosticId}/generate`, {
      modules,
      reportId,
    });
  }

  async getModularGenerationStatus(
    diagnosticId: number,
    reportId: number
  ): Promise<
    import("@/types/ai-generation.types").ModularGenerationStatusResponse
  > {
    return await this.get(
      `api/diagnostics/${diagnosticId}/modular-generation-status?reportId=${reportId}`
    );
  }

  async generateReportDocx(reportId: number): Promise<Blob> {
    const params: Options = {
      timeout: 120 * 1000,
    };
    return await this.client
      .get(`api/reports/${reportId}/generate`, params)
      .blob();
  }

  async fetchDiagnosticImages(diagnosticId: number): Promise<FileEntity[]> {
    return await this.get(`api/diagnostics/${diagnosticId}/images`);
  }

  async fetchDiagnosticSectionsTree(diagnosticId: number): Promise<Section[]> {
    return await this.get(`api/diagnostics/${diagnosticId}/sections`);
  }

  async fetchReportSectionsTree(reportId: number): Promise<Section[]> {
    return await this.get(`api/reports/${reportId}/sections`);
  }

  async reorderDiagnosticAppendices(diagnosticId: number): Promise<void> {
    await this.put(`api/diagnostics/${diagnosticId}/appendices/reorder`);
  }

  // REPORTS
  async fetchDiagnosticReports(diagnosticId: number): Promise<Report[]> {
    return await this.get(`api/diagnostics/${diagnosticId}/reports`);
  }

  async setDefaultReport(
    diagnosticId: number,
    reportId: number | null
  ): Promise<void> {
    return await this.put(`api/diagnostics/${diagnosticId}/default-report`, {
      reportId,
    });
  }

  async createReport(diagnosticId: number, name?: string): Promise<Report> {
    return await this.post(`api/diagnostics/${diagnosticId}/reports`, { name });
  }

  async fetchReport(reportId: number): Promise<Report> {
    return await this.get(`api/reports/${reportId}`);
  }

  async updateReport(
    reportId: number,
    payload: UpdateReportPayload
  ): Promise<Report> {
    return await this.put(`api/reports/${reportId}`, payload);
  }

  async deleteReport(reportId: number): Promise<void> {
    await this.delete(`api/reports/${reportId}`);
  }

  // IQOA Association
  async startIQOAAssociation(
    reportId: number,
    payload: { catalogueIds: number[]; replaceExisting: boolean }
  ): Promise<{ started: boolean }> {
    return await this.post(`api/iqoa/reports/${reportId}/start`, payload);
  }

  async getIQOAAssociationStatus(reportId: number): Promise<{
    isProcessing: boolean;
    progress: { current: number; total: number };
    error: string | null;
    lastCompletedAt: string | null;
  }> {
    return await this.get(`api/iqoa/reports/${reportId}/status`);
  }

  // Conclusions Generation (AI-generated conclusion + preconisation)
  async startConclusionsGeneration(
    reportId: number
  ): Promise<{ started: boolean }> {
    return await this.post(
      `api/conclusions-generation/reports/${reportId}/start`,
      {}
    );
  }

  async getConclusionsGenerationStatus(reportId: number): Promise<{
    isProcessing: boolean;
    error: string | null;
    lastCompletedAt: string | null;
  }> {
    return await this.get(
      `api/conclusions-generation/reports/${reportId}/status`
    );
  }

  async fetchReportFindings(reportId: number): Promise<Finding[]> {
    return await this.get(`api/reports/${reportId}/findings`);
  }

  // SECTIONS
  async createSection(payload: CreateSectionPayload): Promise<Section> {
    return await this.post(`api/sections`, payload);
  }

  async updateSection(
    id: number,
    payload: UpdateSectionPayload
  ): Promise<Section> {
    return await this.put(`api/sections/${id}`, payload);
  }

  async moveSection(
    id: number,
    newOrder: number,
    newParent: number | null
  ): Promise<Section> {
    return await this.put(`api/sections/${id}/move`, { newOrder, newParent });
  }

  async addImageToSection(sectionId: number, fileId: number): Promise<Section> {
    return await this.post(`api/sections/${sectionId}/image`, {
      fileId,
    });
  }

  async fetchSectionFindings(sectionId: number) {
    return await this.get<Finding[]>(`api/sections/${sectionId}/findings`);
  }

  async deleteSection(id: number): Promise<void> {
    await this.delete(`api/sections/${id}`);
  }

  async duplicateSection(id: number): Promise<Section> {
    return await this.post(`api/sections/${id}/duplicate`);
  }

  async createSectionsFromCatalogue(
    diagnosticId: number,
    parentSectionId: number | null,
    catalogueId: number
  ): Promise<Section[]> {
    return await this.post(`api/sections/from-catalogue`, {
      diagnosticId,
      parentSectionId,
      catalogueId,
    });
  }

  async applySectionTemplate(
    reportId: number,
    templateId: number,
    parentSectionId?: number | null
  ): Promise<Section[]> {
    return await this.post(`api/sections/apply-template`, {
      reportId,
      templateId,
      parentSectionId,
    });
  }

  async saveSectionsAsTemplate(
    reportId: number,
    name: string,
    description?: string,
    parentSectionId?: number | null
  ): Promise<any> {
    return await this.post(`api/sections/save-as-template`, {
      reportId,
      name,
      description,
      parentSectionId,
    });
  }

  // CATALOGUES

  async fetchEnabledCatalogues(): Promise<Catalogue[]> {
    return await this.get(`api/catalogues`);
  }

  async fetchCataloguePartsTree(id: number): Promise<CataloguePart[]> {
    return await this.get(`api/catalogues/${id}/tree`);
  }

  async fetchDisorderTemplates(
    searchParams: PaginationParams & FetchDisorderTemplatesFilters
  ): Promise<{
    disorders: DisorderTemplate[];
    totalCount: number;
  }> {
    return await this.get(`api/catalogues/disorder-templates`, {
      searchParams,
    });
  }

  // REPORT TEMPLATES
  async fetchManyReportTemplates(
    params: PaginationParams & { projectId?: number }
  ): Promise<{ templates: ReportTemplate[]; totalCount: number }> {
    return await this.get(`api/report-templates`, {
      searchParams: params,
    });
  }

  async uploadReportTemplate(file: File, projectId?: number): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);

    return this.client.post(`api/report-templates/upload`, {
      body: formData,
      searchParams: projectId ? { projectId } : {},
    });
  }

  async fetchOneReportTemplate(id: number): Promise<ReportTemplate> {
    return await this.get(`api/report-templates/${id}`);
  }

  async updateReportTemplate(
    id: number,
    payload: UpdateReportTemplatePayload
  ): Promise<ReportTemplate> {
    return await this.put(`api/report-templates/${id}`, payload);
  }

  async deleteReportTemplate(id: number): Promise<void> {
    await this.delete(`api/report-templates/${id}`);
  }

  // FILES

  async downloadFile(fileId: number): Promise<Blob> {
    return await this.client.get(`api/files/${fileId}/download`).blob();
  }

  async getFileUrl(fileId: number): Promise<{ url: string }> {
    return await this.get<{ url: string }>(`api/files/${fileId}/url`);
  }

  async uploadFilePresignedBatch(
    items: {
      file: File;
      fileType: FileType;
      params: FileParams;
    }[]
  ): Promise<{ key: string; fileId: number; uploadUrl: string }[]> {
    return await this.post<
      {
        uploadUrl: string;
        key: string;
        fileId: number;
      }[]
    >("api/files/presigned", {
      files: items.map((item) => ({
        originalName: item.file.name,
        mimeType: item.file.type,
        size: item.file.size,
        fileType: item.fileType,
        params: item.params,
      })),
    });
  }

  async finalizeFileUpload(
    files: {
      fileId: number;
      success: boolean;
      fileType: FileType;
      params: FileParams;
    }[]
  ): Promise<FinalizeUploadResponse[]> {
    return await this.post<FinalizeUploadResponse[]>(
      `api/files/finalize/batch`,
      {
        files,
      }
    );
  }

  // HEALTH
  async getHealth(): Promise<ApiHealth> {
    return await this.get<ApiHealth>("health");
  }

  // CHATBOT
  async sendChatbotMessage(
    message: string,
    conversationId?: number | null
  ): Promise<{ reply: string; conversationId: number }> {
    return this.post("api/chatbot", {
      message,
      ...(conversationId ? { conversationId } : {})
    });
  }

  // FEEDBACK
  async sendHandwritingIncompatibleAlert(payload: {
    error: string;
    userAgent?: string;
    platform?: string;
  }): Promise<void> {
    await this.post("api/alerts/handwriting-incompatible", payload);
  }

  async sendFeedback(payload: {
    message: string;
    view: string;
    type: string;
  }): Promise<void> {
    await this.post("api/feedbacks", payload);
  }

  // RECOVERY
  async submitVsfxRecovery(
    drawingId: number,
    branchId: number,
    vsfxBuffer: ArrayBuffer
  ): Promise<{ success: boolean; message: string }> {
    const formData = new FormData();
    formData.append("drawingId", String(drawingId));
    formData.append("branchId", String(branchId));
    formData.append(
      "file",
      new Blob([vsfxBuffer], { type: "application/octet-stream" }),
      "recovery.vsfx"
    );

    return this.client
      .post("api/recovery/vsfx", {
        body: formData,
      })
      .json();
  }

  async getChangelog(page = 1, pageSize = 10) {
    return this.get<{
      entries: (ChangelogApiEntry & { isNew: boolean })[];
      total: number;
      page: number;
      pageSize: number;
      unseenCount: number;
    }>("api/changelog", { searchParams: { page, pageSize } });
  }

  async markChangelogSeen() {
    return this.client.patch("api/changelog/mark-seen").json();
  }
}

export type ChangelogApiEntry = {
  id: string;
  version: string;
  date: string;
  entries: { title: string; body: string }[];
  createdAt: string;
};

export class APIError extends Error {
  response: object & { message?: string };
  status: number;
  _error?: Error;

  constructor(status: number, body: object, error?: Error) {
    super(`API responded with status ${status}.`);
    this.status = status;
    this.response = body;
    if (error) {
      this._error = error;
    }
  }

  isAuthError() {
    return this.status === 401;
  }

  static isOffline(error: Error) {
    return error instanceof TypeError && error?.message === "Failed to fetch";
  }

  userMessage() {
    return this.response?.message;
  }
}

/**
 * Replaces the old version of user that is still provided by the back-end
 * @param user
 */

export default new APIClient();
